/**
 * Steps Store Module
 *
 * @description Store state, mutations, actions and getters for steps.
 */

import Vue from 'vue';
import Vuex from 'vuex';
import {requestStepsBase} from '../../../requests/requestStepsBase';
import {requestAvailableTimesSingleBooking} from '../../../requests/requestAvailableTimesSingleBooking';
import {requestCreateBooking} from '../../../requests/requestCreateBooking';
import {requestSetTransient} from '../../../requests/requstSetTransient';
import Global from '../../../../js/shared/global';

Vue.use(Vuex);

export default {
	namespaced: false,
	state: {
		title: '',
		bookingSteps: [],
		currentStep: 'BlockStepOptions',
		nextStep: 'BlockStepSize',
		previousStep: null,
		allowedPostCodes: [],
		stepCount: 1,
		products: [],
		cleaningType: [],
		frequency: {},
		activeFrequency: '',
		optionsStepTitle: '',
		activeBedroom: {},
		activeBathroom: {},
		dates: {},
		activeTime: '',
		activeProduct: {},
		oneDayDate: '',
		availableTimesSingleBooking: {},
		selectedDateObject: {},
		multiDatesObj: {},
		addressInfo: {},
		blockedDays: [],
		blockedDaysRange: [],
		extras: {},
		policyText: '',
		productsPopup: {},
		total: 0,
		percent: 0,
		validStep: 0,
	},
	mutations: {
		SET_STEPS(state, steps) {
			state.bookingSteps = steps;
			if (steps.locationForm) {
				state.title = steps.locationForm.title;
			}
			if (steps.cleaning_type) {
				state.optionsStepTitle = steps.cleaning_type.cleaning_types_title;
			}
			if (steps.frequency) {
				state.frequency = steps.frequency;
			}
			if (steps.blockedDays) {
				state.blockedDays = steps.blockedDays;
			}
			if (steps.blockedDaysRange) {
				state.blockedDaysRange = steps.blockedDaysRange;
			}
			if (steps.productPopupInfo) {
				state.productsPopup = steps.productPopupInfo;
			}
			state.policyText = steps.stepsPolicyText;
		},
		// products
		SET_PRODUCTS(state, products) {
			state.products = products;
		},
		SET_ACTIVE_PRODUCT(state, product) {
			state.activeProduct = product;
		},
		SET_NEXT_STEP(state, nextStep) {
			state.nextStep = nextStep;
		},
		SET_PREVIOUS_STEP(state, previousStep) {
			state.previousStep = previousStep;
		},
		SET_CURRENT_STEP(state, currentStep) {
			state.currentStep = currentStep;
		},
		SET_VALID_STEP(state, validStep) {
			state.validStep = validStep;
		},
		SET_ALLOWED_POST_CODES(state, allowedPostCodes) {
			state.allowedPostCodes = allowedPostCodes;
		},
		SET_STEP_COUNT(state, stepCount) {
			state.stepCount = stepCount;
		},
		SET_CLEANING_TYPE(state, type) {
			state.cleaningType = type;
		},
		SET_BEDROOM(state, bedroom) {
			state.activeBedroom = bedroom;
		},
		SET_BATHROOM(state, bathroom) {
			state.activeBathroom = bathroom;
		},
		SET_DATES(state, dates) {
			state.dates = dates;
		},
		SET_FREQUENCY(state, frequency) {
			state.frequency = frequency;
		},
		SET_ACTIVE_FREQUENCY(state, frequency) {
			state.activeFrequency = frequency;
		},
		SET_ACTIVE_TIME(state, time) {
			state.activeTime = time;
		},
		SET_ONE_DAY_DATE(state, date) {
			state.oneDayDate = date;
		},
		SET_SELECTED_DATE_OBJECT(state, date) {
			state.selectedDateObject = date;
		},
		SET_AVAILABLE_TIME_SINGLE_BOOKING(state, payload) {
			state.availableTimesSingleBooking = payload;
		},
		SET_MULTI_DATES_OBJ(state, payload) {
			state.multiDatesObj = payload;
		},
		SET_ADDRESS_INFO(state, payload) {
			state.addressInfo = payload;
		},
		SET_TOTAL(state, payload) {
			state.total = payload;
		},
		SET_PERCENT(state, payload) {
			state.percent = payload;
		}
	},
	actions: {
		setSteps(vuexContext) {
			if (vuexContext.getters.getSteps === null) requestStepsBase(vuexContext);
		},
		setActiveProduct(context, activeProduct) {
			context.commit('SET_ACTIVE_PRODUCT', activeProduct);
		},
		setNextStep(context, currentStepName) {
			context.commit('SET_NEXT_STEP', currentStepName);
		},
		setPreviousStep(context, prePreviousStepName) {
			context.commit('SET_PREVIOUS_STEP', prePreviousStepName);
		},
		setCurrentStep(context, previousStepName) {
			context.commit('SET_CURRENT_STEP', previousStepName);
		},
		setValidStep(context, step) {
			context.commit('SET_VALID_STEP', step);
		},
		setStepCount(context, stepCount) {
			context.commit('SET_STEP_COUNT', stepCount);
		},
		setCleaningType(context, type) {
			context.commit('SET_CLEANING_TYPE', type);
		},
		setBedroom(context, bedroom) {
			context.commit('SET_BEDROOM', bedroom);
		},
		setBathroom(context, bathroom) {
			context.commit('SET_BATHROOM', bathroom);
		},
		setFrequency(context, frequency) {
			context.commit('SET_FREQUENCY', frequency);
		},
		setDates(context, dates) {
			context.commit('SET_DATES', dates);
		},
		setActiveFrequency(context, frequency) {
			context.commit('SET_ACTIVE_FREQUENCY', frequency);
		},
		setActiveTime(context, activeTime) {
			context.commit('SET_ACTIVE_TIME', activeTime);
		},
		setOneDayDate(context, date) {
			context.commit('SET_ONE_DAY_DATE', date);
		},
		setSelectedDateObject(context, date) {
			context.commit('SET_SELECTED_DATE_OBJECT', date);
		},
		setAvailableTimeSingleBooking(context, payload) {
			requestAvailableTimesSingleBooking(this, payload);
		},
		setBookingData(context, payload) {
			requestCreateBooking(this, payload);
		},
		setMultiDatesObj(context, obj) {
			context.commit('SET_MULTI_DATES_OBJ', obj);
		},
		setAddressInfo(context, obj) {
			context.commit('SET_ADDRESS_INFO', obj);
		},
		setTransient(context, obj) {
			requestSetTransient(this, obj);
		},
		setTotal(context, data) {
			const formatter = new Intl.NumberFormat('en-US', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			});
			const pid = Global.readCookie('pid');
			const activeBedroom = Global.readCookie('activeBedroom') ? Global.readCookie('activeBedroom') : '1';
			const activeBathroom = Global.readCookie('activeBathroom') ? Global.readCookie('activeBathroom') : '1';
			let productID = context.getters.getCleaningType.productId;
			if (!productID) {
				productID = pid;
			}
			const currentProduct = context.getters.getProducts.filter(function(x) {
				if (x.id === productID) {
					return x;
				}
			});

			const bathroomPrice = currentProduct[0].bathrooms[parseInt(activeBathroom) - 1].price;
			const bedroomPrice = currentProduct[0].bedrooms[parseInt(activeBedroom) - 1].price;
			const extraPriceObj = Global.readCookie('extraPriceTotal');

			let extraPrice = 0;
			if (extraPriceObj) {
				for (let i = 0; i < extraPriceObj.children.length; i++) {
					if (!extraPriceObj.children[i].included) {
						extraPrice = parseFloat(extraPrice) + parseFloat(extraPriceObj.children[i].val);
					}
				}
			}
			let total = parseFloat(bathroomPrice) + parseFloat(bedroomPrice) + parseFloat(extraPrice);
			total = formatter.format(total);
			let sum = parseFloat(total);
			let dec = '';
			let percent = 0;
			console.log(data);
			if (data.often === 'weekly') {
				percent = (parseFloat(total) / 100) * 10;
			}
			sum = sum - percent;
			if (data.often === 'twiceAweek') {
				sum = sum * 2;
			}
			if (sum % 1 === 0) {
				dec = '.00';
			}
			context.commit('SET_TOTAL', sum.toString() + dec);
		},
		setPercent(context, data) {
			context.commit('SET_PERCENT', data);
		}
	},
	getters: {
		getTitle(state) {
			return state.title ? state.title : 'Choose your location';
		},
		getOptionsStepTitle(state) {
			return state.optionsStepTitle ? state.optionsStepTitle : 'Choose options';
		},
		getSteps(state) {
			return state.bookingSteps;
		},
		getNextStep(state) {
			return state.nextStep;
		},
		getPreviousStep(state) {
			return state.previousStep;
		},
		getCurrentStep(state) {
			return state.currentStep;
		},
		getAllowedPostCodes(state) {
			return state.allowedPostCodes;
		},
		getStepCount(state) {
			return state.stepCount;
		},
		getProducts(state) {
			return state.products;
		},
		getProductsPopup(state) {
			return state.productsPopup;
		},
		getCleaningType(state) {
			return state.cleaningType;
		},
		getBedroom(state) {
			return state.activeBedroom;
		},
		getBathroom(state) {
			return state.activeBathroom;
		},
		getDates(state) {
			return state.dates;
		},
		getFrequency(state) {
			return state.frequency;
		},
		getActiveFrequency(state) {
			return state.activeFrequency;
		},
		getActiveTime(state) {
			return state.activeTime;
		},
		getActiveProduct(state) {
			return state.activeProduct;
		},
		getOneDayDate(state) {
			return state.oneDayDate;
		},
		getAvailableTimeSingleBooking(state) {
			return state.availableTimesSingleBooking;
		},
		getSelectedDateObject(state) {
			return state.selectedDateObject;
		},
		getMultiDatesObj(state) {
			return state.multiDatesObj;
		},
		getBlockedDays(state) {
			return state.blockedDays;
		},
		getBlockedDaysRange(state) {
			return state.blockedDaysRange;
		},
		getExtras(state) {
			return state.extras;
		},
		getCount(state) {
			return state.stepCount;
		},
		getPolicyText(state) {
			return state.policyText;
		},
		getTotal(state) {
			return state.total;
		},
		getPercent(state) {
			return state.percent;
		},
		getValidStep(state) {
			return state.validStep;
		}
	}
};
